import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Clock, AlertTriangle, CheckCircle2, Upload, X, FileText, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocUpload = ({ label, file, onSelect, onClear, testid }) => {
  const ref = useRef(null);
  return (
    <div className="space-y-1.5">
      <Label className="text-gray-300 text-sm">{label}</Label>
      {file ? (
        <div className="flex items-center justify-between bg-gray-800 rounded-lg px-3 py-2 border border-gray-700">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-gray-300 text-sm truncate">{file.name}</span>
          </div>
          <button onClick={onClear} className="text-gray-400 hover:text-white shrink-0" data-testid={`${testid}-clear`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-700 rounded-lg py-3 text-gray-400 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors"
          data-testid={testid}
        >
          <Upload className="w-4 h-4" /> Upload
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => e.target.files?.[0] && onSelect(e.target.files[0])}
      />
    </div>
  );
};

export const DriverOnboarding = ({ driver, token, onDone }) => {
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    bank_bsb: driver?.bank_bsb || '',
    bank_account: driver?.bank_account || '',
    abn: driver?.abn || '',
    emergency_contact_name: driver?.emergency_contact_name || '',
    emergency_contact_phone: driver?.emergency_contact_phone || '',
  });
  const [files, setFiles] = useState({ licence_front: null, licence_back: null, insurance_proof: null });

  if (!driver) return null;

  const pending = driver.approval_status === 'pending';
  const rejected = driver.approval_status === 'rejected';
  const incomplete = !driver.onboarding_complete;

  // Nothing to show for an approved, fully onboarded driver
  if (!pending && !rejected && !incomplete) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      if (files.licence_front) fd.append('licence_front', files.licence_front);
      if (files.licence_back) fd.append('licence_back', files.licence_back);
      if (files.insurance_proof) fd.append('insurance_proof', files.insurance_proof);

      const res = await axios.post(`${API}/drivers/onboarding`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      (res.data.flags || []).forEach((f) => toast.warning(f, { duration: 7000 }));
      if (res.data.onboarding_complete) {
        toast.success('Onboarding complete! Your account is now awaiting admin approval.');
      } else {
        toast.success('Details saved. Complete the remaining items to finish onboarding.');
      }
      setShowModal(false);
      onDone?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save onboarding details');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Status banner in sidebar */}
      <div className="mx-4 mb-3" data-testid="onboarding-banner">
        {rejected ? (
          <div className="rounded-lg border border-red-800 bg-red-950/60 p-3">
            <div className="flex items-center gap-2 text-red-300 text-sm font-semibold mb-1">
              <AlertTriangle className="w-4 h-4" /> Application rejected
            </div>
            <p className="text-red-200/80 text-xs">{driver.rejection_reason || 'Please contact support for details.'}</p>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-800 bg-amber-950/50 p-3">
            <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold mb-1">
              {pending ? <Clock className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
              {pending ? 'Pending approval' : 'Onboarding incomplete'}
            </div>
            <p className="text-amber-200/80 text-xs mb-2">
              {incomplete
                ? 'Complete your payment, emergency contact and document details before your first shift.'
                : 'An admin is reviewing your details. You can\u2019t receive orders until approved.'}
            </p>
            {incomplete && (
              <Button
                size="sm"
                onClick={() => setShowModal(true)}
                className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 h-8 text-xs font-semibold"
                data-testid="open-onboarding-btn"
              >
                Complete onboarding
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Onboarding modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="onboarding-modal">
          <div className="bg-gray-900 rounded-2xl w-full max-w-lg border border-gray-800 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
              <div>
                <h3 className="text-lg font-semibold text-white">Complete Onboarding</h3>
                <p className="text-gray-500 text-xs">Required before your first shift</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 hover:text-white" data-testid="close-onboarding-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-5">
              {/* Payment */}
              <div>
                <h4 className="text-white font-medium mb-2 flex items-center gap-2 text-sm">Payment details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">BSB</Label>
                    <Input value={form.bank_bsb} onChange={(e) => set('bank_bsb', e.target.value)}
                      placeholder="062-000" className="bg-gray-800 border-gray-700 text-white" data-testid="onboarding-bsb-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Account number</Label>
                    <Input value={form.bank_account} onChange={(e) => set('bank_account', e.target.value)}
                      placeholder="12345678" className="bg-gray-800 border-gray-700 text-white" data-testid="onboarding-account-input" />
                  </div>
                </div>
                <div className="space-y-1.5 mt-3">
                  <Label className="text-gray-300 text-sm">ABN <span className="text-gray-500">(optional)</span></Label>
                  <Input value={form.abn} onChange={(e) => set('abn', e.target.value)}
                    placeholder="11 digit ABN" className="bg-gray-800 border-gray-700 text-white" data-testid="onboarding-abn-input" />
                  <p className="text-gray-500 text-xs">Most AU delivery drivers are sole traders. Without an ABN there may be tax implications for the merchant paying you.</p>
                </div>
              </div>

              {/* Emergency contact */}
              <div>
                <h4 className="text-white font-medium mb-2 text-sm">Emergency contact</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Name</Label>
                    <Input value={form.emergency_contact_name} onChange={(e) => set('emergency_contact_name', e.target.value)}
                      placeholder="Contact name" className="bg-gray-800 border-gray-700 text-white" data-testid="onboarding-emergency-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Phone</Label>
                    <Input value={form.emergency_contact_phone} onChange={(e) => set('emergency_contact_phone', e.target.value)}
                      placeholder="+61 400 000 000" className="bg-gray-800 border-gray-700 text-white" data-testid="onboarding-emergency-phone-input" />
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="space-y-3">
                <h4 className="text-white font-medium text-sm">Document uploads</h4>
                <DocUpload label="Driver's licence (front)" file={files.licence_front}
                  onSelect={(f) => setFiles((p) => ({ ...p, licence_front: f }))}
                  onClear={() => setFiles((p) => ({ ...p, licence_front: null }))} testid="onboarding-licence-front" />
                <DocUpload label="Driver's licence (back)" file={files.licence_back}
                  onSelect={(f) => setFiles((p) => ({ ...p, licence_back: f }))}
                  onClear={() => setFiles((p) => ({ ...p, licence_back: null }))} testid="onboarding-licence-back" />
                <DocUpload label="Proof of vehicle insurance" file={files.insurance_proof}
                  onSelect={(f) => setFiles((p) => ({ ...p, insurance_proof: f }))}
                  onClear={() => setFiles((p) => ({ ...p, insurance_proof: null }))} testid="onboarding-insurance" />
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-950/40 border border-amber-900 text-amber-200/90 text-xs">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Standard personal vehicle insurance often does <strong>not</strong> cover commercial delivery use. Make sure your policy covers commercial use.</span>
                </div>
              </div>

              <Button onClick={handleSubmit} disabled={isSaving}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="submit-onboarding-btn">
                {isSaving ? 'Saving...' : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Save onboarding details</>)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
